import { createExternalMediaJob, presignExternalMediaPart, putPartDirectToR2, finalizeExternalMediaJob } from "./externalMediaUpload";

// Use the ordered teaching route, never the pickup or home/return address.
export function closingWords(route) {
  const signOff = "Remember, slow is control, and safety is the priority. Have a nice drive.";
  const waypoints = route?.navigation_waypoints;
  if (route?.return_mode) {
    return "The return route is not confirmed yet. Please check the route while parked before setting off.";
  }
  if (!route?.route_geometry || !Array.isArray(waypoints)) {
    return "Today's teaching route is not confirmed yet. Please agree and check the destination while parked before setting off.";
  }
  const returnIndex = waypoints.findIndex(point => point?.kind === "return");
  const outward = returnIndex > 0 ? waypoints.slice(0, returnIndex) : [];
  const destination = outward.at(-1);
  const name = typeof destination?.name === "string" ? destination.name.trim() : "";
  if (destination?.kind !== "challenge" || !name || name.length > 160) {
    return "Today's teaching destination is not confirmed yet. Please check the route while parked before setting off.";
  }
  return `We're heading towards ${name} before turning back. ${signOff}`;
}

export function openingWords(goal, level, support) {
  const challenge = level == null ? "We can use challenges across levels one to four as appropriate." : `Today's chosen challenge is level ${level}.`;
  const help = support == null ? "Agree together how much help is wanted." : `The requested support is ${support} percent. Check together what that means today.`;
  return `Today's starting goal is ${goal || "Getting to know your driving"}. ${challenge} ${help} You can change the goal with Adapt. While parked, I'm going to start recording your briefing. Tell me what you want to work on and what support would help. Tap End when you have finished.`;
}

export function isOpeningDraftOutcomeUnknown(error) {
  return error?.code === "OPENING_DRAFT_OUTCOME_UNKNOWN";
}

// Short capture only. Real recorder events own the state; a permission request
// or elapsed timer is never reported as a successful recording.
export async function captureOpening({ signal, onRecording, onElapsed, onStopReady, maxSeconds = 60, stream: suppliedStream, mediaDevices = navigator.mediaDevices, Recorder = window.MediaRecorder }) {
  if (!mediaDevices?.getUserMedia || !Recorder) throw new Error("Recording is unavailable in this browser. You can still start the lesson.");
  const stream = suppliedStream || await mediaDevices.getUserMedia({ audio: true });
  const release = () => stream.getTracks().forEach(track => track.stop());
  if (signal.aborted) { release(); throw new Error("Recording cancelled"); }
  return new Promise((resolve, reject) => {
    let recorder, timer, finished = false;
    const chunks = [];
    const finish = (error) => {
      if (finished) return;
      finished = true;
      clearInterval(timer);
      signal.removeEventListener("abort", cancel);
      release();
      const blob = new Blob(chunks, { type: recorder?.mimeType || chunks[0]?.type || "audio/webm" });
      if (error || !blob.size) reject(error || new Error("No audio was captured. Retry or continue without a recording."));
      else resolve(blob);
    };
    const stop = () => { if (recorder?.state === "recording") recorder.stop(); };
    const cancel = () => { stop(); finish(new Error("Recording cancelled")); };
    try {
      const mimeType = ["audio/mp4", "audio/webm;codecs=opus", "audio/webm"].find(type => Recorder.isTypeSupported?.(type));
      recorder = new Recorder(stream, mimeType ? { mimeType } : undefined);
      recorder.ondataavailable = event => { if (event.data?.size) chunks.push(event.data); };
      recorder.onerror = () => finish(new Error("The microphone stopped unexpectedly. Retry or continue without a recording."));
      recorder.onstop = () => finish();
      recorder.onstart = () => {
        if (finished || signal.aborted) return;
        const started = Date.now();
        onRecording();
        onStopReady(stop);
        timer = setInterval(() => {
          const seconds = Math.floor((Date.now() - started) / 1000);
          onElapsed(maxSeconds == null ? seconds : Math.min(maxSeconds, seconds));
          if (maxSeconds != null && seconds >= maxSeconds) stop();
        }, 250);
      };
      signal.addEventListener("abort", cancel, { once: true });
      recorder.start(1000);
    } catch (error) { finish(error); }
  });
}

// Capture drafts are scoped by authenticated owner AND exact Work Mode session.
// They survive reload/upload failure and are deleted only after server receipt.
export async function openingDraft(action, key, value) {
  return new Promise((resolve, reject) => {
    if (!window.indexedDB) { reject(new Error("Local recording storage is unavailable")); return; }
    let request, database, transaction, operation, transactionError, timeout, completionTimeout, timedOut = false, settled = false;
    const timeoutError = new Error("Local recording storage timed out");
    const unknownOutcomeError = cause => {
      const error = new Error("Local recording storage outcome could not be confirmed");
      error.name = "OpeningDraftOutcomeUnknownError";
      error.code = "OPENING_DRAFT_OUTCOME_UNKNOWN";
      if (cause) error.cause = cause;
      return error;
    };
    const close = () => {
      if (!database) return;
      const connection = database;
      database = null;
      connection.close();
    };
    const settle = (error, result) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      clearTimeout(completionTimeout);
      close();
      if (error) reject(error);
      else resolve(result);
    };
    try {
      request = window.indexedDB.open("ndg-lesson-opening", 1);
    } catch (error) {
      settle(error);
      return;
    }
    timeout = setTimeout(() => {
      if (settled) return;
      timedOut = true;
      if (!transaction) { settle(timeoutError); return; }
      try {
        transaction.abort();
        // A successful abort rolls back the transaction before returning;
        // the later abort event is a notification, not a second outcome.
        settle(timeoutError);
      } catch (error) {
        if (error?.name === "InvalidStateError") {
          // The write may already be committing. Observe its terminal event
          // rather than call it an abort; keep this settlement wait bounded.
          completionTimeout = setTimeout(() => settle(unknownOutcomeError(error)), 250);
        } else {
          // An abnormal abort offers no cancellation guarantee. A later
          // exact-key read is ordered behind any still-running write.
          settle(unknownOutcomeError(error));
        }
      }
    }, 5000);
    request.onupgradeneeded = () => request.result.createObjectStore("drafts");
    request.onerror = () => settle(request.error || new Error("Local recording storage failed"));
    request.onsuccess = () => {
      database = request.result;
      if (settled) { close(); return; }
      try {
        transaction = database.transaction("drafts", action === "get" ? "readonly" : "readwrite");
        const store = transaction.objectStore("drafts");
        operation = action === "get" ? store.get(key) : action === "delete" ? store.delete(key) : store.put(value, key);
        transaction.onerror = () => { transactionError = transaction.error || transactionError; };
        transaction.onabort = () => settle(transactionError || (timedOut ? timeoutError : transaction.error) || new Error("Recording storage failed"));
        transaction.oncomplete = () => settle(null, operation.result);
      } catch (error) {
        settle(error);
      }
    };
  });
}

export async function uploadOpening(draft, signal) {
  const extension = draft.blob.type.includes("mp4") ? "m4a" : "webm";
  const file = new File([draft.blob], `lesson-opening-${draft.mediaId}.${extension}`, { type: draft.blob.type });
  const partSize = 8 * 1024 * 1024;
  const totalParts = Math.ceil(file.size / partSize);
  let result = await createExternalMediaJob({
    ndgMediaId: draft.mediaId, file, totalParts, purposeCode: "voice_note",
    fingerprint: draft.fingerprint, signal,
    routingContext: { lesson_id: draft.bookingId || draft.lessonId, learner_id: draft.pupilId,
      captured_at: draft.capturedAt, originating_workflow: "lesson_opening",
      originating_platform_surface: "work_mode", client_capture_context: "parked_goal_and_support" },
  });
  if (result?.job?.state !== "completed") {
    for (let part = 1; part <= totalParts; part++) {
      const signed = await presignExternalMediaPart(draft.mediaId, part, { signal });
      await putPartDirectToR2(signed.upload_url, file.slice((part - 1) * partSize, part * partSize), undefined, { signal });
    }
    result = await finalizeExternalMediaJob(draft.mediaId, { signal });
  }
  if (result?.job?.state !== "completed") throw new Error("The recording service has not confirmed the save. Your local copy is kept.");
  return result;
}
